// Shrink a picked image in the browser before it ever reaches storage.
//
// Why (09.17.2026): staff upload straight off a phone, so the bucket filled
// with 5712x4284 files averaging 3.5MB. Nothing on the site paints above
// 2200px (the lightbox, where someone inspects a slab's corners), so anything
// larger is bytes nobody ever sees — paid for on upload, on storage, and on
// every transform computed from it.
//
// Anything already small enough, or not a still image, passes through
// untouched. If the browser can't decode it for any reason, the original
// uploads as before: a slow upload beats a failed one.
const MAX_EDGE = 2400;
const QUALITY = 0.85;

export async function shrinkImage(file) {
  if (!file.type.startsWith('image/')) return file;
  if (file.type === 'image/gif') return file;

  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    const longest = Math.max(width, height);
    if (longest <= MAX_EDGE) {
      bitmap.close();
      return file;
    }

    const scale = MAX_EDGE / longest;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', QUALITY)
    );
    if (!blob || blob.size >= file.size) return file;

    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return file;
  }
}
